import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { signIn, signUp } from '../lib/supabase-auth';
import { supabase } from '../lib/supabase';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: 'sign-in' | 'sign-up';
}

interface ModalContent {
  [key: string]: string;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, onSuccess, mode: initialMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'sign-in' | 'sign-up'>(initialMode);
  const [content, setContent] = useState<ModalContent>({});

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    const loadContent = async () => {
      const { data, error } = await supabase
        .from('landing_content')
        .select('key, content')
        .eq('section', 'auth_modal');

      if (!error && data) {
        const contentMap = data.reduce((acc: ModalContent, item) => {
          acc[item.key] = item.content;
          return acc;
        }, {});
        setContent(contentMap);
      }
    };

    loadContent();
  }, []);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { user, error } = mode === 'sign-up'
        ? await signUp(email, password)
        : await signIn(email, password);

      if (error) {
        if (error.message === 'user_already_exists') {
          setError('This email is already registered. Please sign in instead.');
          setMode('sign-in');
        } else if (error.message === 'Invalid login credentials') {
          setError('Invalid email or password. Please try again.');
        } else {
          setError(error.message);
        }
        return;
      }

      if (user) {
        onSuccess();
        onClose();
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(current => current === 'sign-in' ? 'sign-up' : 'sign-in');
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="text-2xl font-semibold mb-6 bg-gradient-to-r from-[#a85ff7] to-[#e457b5] bg-clip-text text-transparent">
          {mode === 'sign-up' 
            ? content.sign_up_title || 'Create your account'
            : content.sign_in_title || 'Welcome back'
          }
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {content.email_label || 'Email address'}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a85ff7] focus:border-transparent"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {content.password_label || 'Password'}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a85ff7] focus:border-transparent"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 bg-gradient-to-r from-[#a85ff7] to-[#e457b5] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-medium shadow-md"
          >
            {isLoading
              ? 'Please wait...'
              : mode === 'sign-up'
                ? (content.sign_up_button || 'Create account')
                : (content.sign_in_button || 'Sign in')
            }
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={toggleMode}
            className="text-sm text-[#a85ff7] hover:text-[#e457b5] transition-colors"
          >
            {mode === 'sign-up'
              ? (content.switch_to_sign_in || 'Already have an account? Sign in')
              : (content.switch_to_sign_up || "Don't have an account? Sign up")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;